import { useFarmThresholds } from '@/hooks/useFarmThresholds';
import { Fragment, type ReactNode } from 'react';
import {
  Dialog as HeadlessDialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { useNavigate } from '@tanstack/react-router';
import { X, AlertTriangle, XCircle, ArrowRight, Lightbulb } from 'lucide-react';
import { cn } from '@/lib/cn';
import { paramColor } from '@/lib/paramColor';
import { toNumberOrNull } from '@/lib/number';
import { StatusIcon } from '@/components/ui/StatusIcon';
import { generateAdvice } from '@/lib/advice';
import { format, parseISO } from 'date-fns';
import type { PondOverview, AlertThresholds } from '@/types/api';
import { Term } from '@/components/ui/Term';

interface PondQuickViewModalProps {
  pond: PondOverview | null;
  open: boolean;
  onClose: () => void;
  alertThresholds?: AlertThresholds | null;
}

export function PondQuickViewModal({
  pond,
  open,
  onClose,
  alertThresholds,
}: PondQuickViewModalProps) {
  const navigate = useNavigate();
  const { table } = useFarmThresholds();

  if (!pond) return null;

  const isActive = pond.status === 'active';
  const cycle = pond.active_cycle;
  const reading = cycle?.latest_reading;
  const sampling = cycle?.sampling;
  const lastFeeding = cycle?.last_feeding;
  const alertCount = pond.alert_count ?? 0;

  const getStatus = (): 'ok' | 'warning' | 'critical' | 'pending' => {
    if (alertCount > 0) return 'critical';
    if (!isActive) return 'pending';
    if (reading?.has_alert) return 'warning';
    return 'ok';
  };

  const status = getStatus();

  const accentColor = {
    ok: 'bg-status-ok',
    warning: 'bg-status-warning',
    critical: 'bg-status-critical',
    pending: 'bg-gray-400',
  };

  const handleViewDetails = () => {
    onClose();
    navigate({ to: '/ponds/$pondId', params: { pondId: pond.id } });
  };

  // Build subtitle parts
  const subtitleParts: string[] = [];
  if (cycle) {
    subtitleParts.push(`DOC ${cycle.doc}`);
    if (cycle.species) subtitleParts.push(cycle.species);
    if (cycle.stocking_date) {
      try {
        subtitleParts.push(`Stocked ${format(parseISO(cycle.stocking_date), 'MMM d')}`);
      } catch {
        // ignore parse error
      }
    }
  }

  return (
    <Transition appear show={open} as={Fragment}>
      <HeadlessDialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-[480px] transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                {/* Accent bar */}
                <div className={cn('h-1 rounded-t-2xl', accentColor[status])} />

                {/* Header */}
                <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <StatusIcon status={status} size="lg" />
                    <div className="min-w-0">
                      <h3 className="text-lg font-bold text-gray-900 truncate">
                        {pond.name}
                      </h3>
                      {subtitleParts.length > 0 ? (
                        <p className="text-sm text-gray-500">
                          {subtitleParts.join(' \u00B7 ')}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-400 capitalize">{pond.status}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="shrink-0 p-1.5 rounded-lg border border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {/* Body */}
                {isActive && cycle ? (
                  <div className="px-5 pb-5">
                    {/* Alert banners */}
                    {pond.alerts && pond.alerts.length > 0 && (
                      <div className="space-y-2 mb-4">
                        {pond.alerts.map((alert, i) => (
                          <div
                            key={i}
                            className={cn(
                              'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium',
                              alert.severity === 'critical'
                                ? 'bg-red-50 text-red-700'
                                : 'bg-amber-50 text-amber-700'
                            )}
                          >
                            {alert.severity === 'critical' ? (
                              <XCircle className="h-4 w-4 shrink-0" />
                            ) : (
                              <AlertTriangle className="h-4 w-4 shrink-0" />
                            )}
                            {alert.message}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Stats grid */}
                    {sampling && (
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <StatCard
                          label={<Term term="ABW" />}
                          value={toNumberOrNull(sampling.abw_g)}
                          unit="g"
                          trend={toNumberOrNull(sampling.weekly_gain_g)}
                          trendUnit="g/wk"
                        />
                        <StatCard
                          label="Survival"
                          value={toNumberOrNull(sampling.survival_rate_pct)}
                          unit="%"
                        />
                        <StatCard
                          label={<Term term="FCR" />}
                          value={toNumberOrNull(sampling.fcr)}
                        />
                      </div>
                    )}

                    {/* Readings row */}
                    {reading && (
                      <div className="grid grid-cols-4 gap-px bg-gray-100 rounded-lg overflow-hidden mb-4">
                        <ReadingCell
                          label="pH"
                          value={toNumberOrNull(reading.ph)}
                          decimals={1}
                          colorFn={(v) => paramColor(table, 'ph', v, alertThresholds?.ph_min, alertThresholds?.ph_max, 0.5)}
                        />
                        <ReadingCell
                          label="DO"
                          value={toNumberOrNull(reading.dissolved_oxygen)}
                          unit="mg/L"
                          decimals={1}
                          colorFn={(v) => paramColor(table, 'dissolvedOxygen', v, alertThresholds?.do_min, alertThresholds?.do_max, 1)}
                        />
                        <ReadingCell
                          label="Temp"
                          value={toNumberOrNull(reading.temperature_c)}
                          unit="°C"
                          decimals={1}
                          colorFn={(v) => paramColor(table, 'temperature', v, alertThresholds?.temp_min, alertThresholds?.temp_max, 2)}
                        />
                        <ReadingCell
                          label="Salinity"
                          value={toNumberOrNull(reading.salinity_ppt)}
                          unit="ppt"
                          decimals={1}
                          colorFn={(v) => paramColor(table, 'salinity', v, alertThresholds?.salinity_min, alertThresholds?.salinity_max, 2)}
                        />
                      </div>
                    )}

                    {/* Advice (top 2 items) */}
                    {reading && (() => {
                      const adviceItems = generateAdvice(
                        {
                          ph: toNumberOrNull(reading.ph),
                          dissolved_oxygen: toNumberOrNull(reading.dissolved_oxygen),
                          temperature_c: toNumberOrNull(reading.temperature_c),
                          salinity_ppt: toNumberOrNull(reading.salinity_ppt),
                          ammonia_ppm: toNumberOrNull(reading.ammonia_ppm),
                          nitrite_ppm: toNumberOrNull(reading.nitrite_ppm),
                        },
                        cycle.doc,
                        undefined,
                        table,
                      );
                      if (adviceItems.length === 0) return null;
                      const topItems = adviceItems.slice(0, 2);
                      return (
                        <div className="space-y-1.5 mb-4">
                          {topItems.map((item) => (
                            <div
                              key={item.id}
                              className={cn(
                                'flex items-start gap-2 px-3 py-2 rounded-lg text-xs',
                                item.severity === 'critical'
                                  ? 'bg-red-50 text-red-700'
                                  : item.severity === 'warning'
                                    ? 'bg-amber-50 text-amber-700'
                                    : 'bg-blue-50 text-blue-700'
                              )}
                            >
                              <Lightbulb className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                              <div>
                                <span className="font-medium">{item.title}:</span>{' '}
                                {item.action}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="text-sm text-gray-500">
                        {lastFeeding ? (
                          <>
                            Last feed: <span className="font-medium text-gray-700">{toNumberOrNull(lastFeeding.feed_amount_kg)?.toFixed(1) ?? '—'} kg</span>
                            {lastFeeding.feed_type && (
                              <span className="text-gray-400"> ({lastFeeding.feed_type})</span>
                            )}
                            <span className="text-gray-400"> at {lastFeeding.feeding_slot}</span>
                          </>
                        ) : (
                          <span className="text-gray-400">No feeding data</span>
                        )}
                      </div>
                      <button
                        onClick={handleViewDetails}
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors"
                      >
                        View Details
                        <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Idle state */
                  <div className="px-5 pb-5">
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <p className="text-gray-400 italic">
                        {pond.status === 'preparing' ? 'Preparing' : 'No active cycle'}
                      </p>
                    </div>
                    <div className="flex justify-end pt-3 border-t border-gray-100">
                      <button
                        onClick={handleViewDetails}
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors"
                      >
                        View Details
                        <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </HeadlessDialog>
    </Transition>
  );
}

// --- Internal components ---

interface StatCardProps {
  label: ReactNode;
  value: number | null | undefined;
  unit?: string;
  trend?: number | null | undefined;
  trendUnit?: string;
}

function StatCard({ label, value, unit, trend, trendUnit }: StatCardProps) {
  const displayValue = typeof value === 'number' ? value.toFixed(1) : '—';
  const isPositiveTrend = typeof trend === 'number' && trend > 0;

  return (
    <div className="bg-gray-50 rounded-lg p-3 text-center">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-lg font-bold text-gray-900">
        {displayValue}
        {unit && typeof value === 'number' && (
          <span className="text-sm font-normal text-gray-500 ml-0.5">{unit}</span>
        )}
      </p>
      {typeof trend === 'number' && (
        <p className={cn('text-xs mt-0.5', isPositiveTrend ? 'text-green-600' : 'text-gray-400')}>
          {isPositiveTrend ? '+' : ''}{trend.toFixed(1)} {trendUnit}
        </p>
      )}
    </div>
  );
}

interface ReadingCellProps {
  label: string;
  value: number | null | undefined;
  unit?: string;
  decimals?: number;
  colorFn?: (value: number) => 'green' | 'amber' | 'red' | 'gray';
}

function ReadingCell({ label, value, unit, decimals = 1, colorFn }: ReadingCellProps) {
  const color = typeof value === 'number' && colorFn ? colorFn(value) : 'gray';
  const displayValue = typeof value === 'number' ? value.toFixed(decimals) : '—';

  const colorClasses = {
    green: 'text-green-600',
    amber: 'text-amber-600',
    red: 'text-red-600',
    gray: 'text-gray-400',
  };

  return (
    <div className="bg-white p-3 text-center">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={cn('text-sm font-semibold', colorClasses[color])}>
        {displayValue}
      </p>
      {unit && typeof value === 'number' && (
        <p className="text-[10px] text-gray-400">{unit}</p>
      )}
    </div>
  );
}

